import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Progress } from '@/components/ui/progress';
import { Loader2, ImageIcon, AlertCircle, CheckCircle2, Maximize2 } from 'lucide-react';
import { PRINT_RATIOS, type PrintRatio } from '@/types';
import apiClient from '@/lib/apiClient';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

interface MockupGeneratorProps {
  productId: string;
  existingMockups: string[];
  printFileUrls: Record<string, string> | null;
  onMockupsUpdated: (mockups: string[]) => void;
}

interface JobStatus {
  jobId: string;
  status: 'pending' | 'processing' | 'complete' | 'failed' | 'cancelled';
  expectedCount: number;
  completedCount: number;
  mockupUrls: string[];
  errorMessage?: string;
}

const POLL_INTERVAL = 5000;

function toCapitalCase(str: string): string {
  return str
    .split(/[-_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function MockupGenerator({
  productId,
  existingMockups,
  printFileUrls,
  onMockupsUpdated,
}: MockupGeneratorProps) {
  const [collections, setCollections] = useState<string[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<string>('');
  const [selectedRatio, setSelectedRatio] = useState<PrintRatio>('3:4');
  const [isGenerating, setIsGenerating] = useState(false);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingCollections, setLoadingCollections] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const pollRunIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      pollRunIdRef.current += 1;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const pollJobStatus = useCallback(async (jobId: string, runId: number) => {
    console.log(`[POLL] Start - runId:${runId} currentRef:${pollRunIdRef.current}`);
    if (runId !== pollRunIdRef.current) {
      console.log(`[POLL] STALE runId, bailing out`);
      return;
    }
    
    try {
      const response = await apiClient.get(`/mockups/status?jobId=${jobId}`);
      
      console.log(`[POLL] Response received - runId:${runId} currentRef:${pollRunIdRef.current}`);
      if (runId !== pollRunIdRef.current || !isMountedRef.current) {
        console.log(`[POLL] STALE after response, bailing out`);
        return;
      }
      
      if (response.data.success) {
        const status = response.data.data as JobStatus;
        
        if (runId !== pollRunIdRef.current) {
          console.log(`[POLL] STALE before setJobStatus, bailing out`);
          return;
        }
        
        console.log(`[POLL] Setting jobStatus - status:${status.status} mockups:${status.mockupUrls.length}`);
        setJobStatus(status);

        if (status.mockupUrls.length > 0) {
          onMockupsUpdated(status.mockupUrls);
        }

        if (status.status === 'complete') {
          console.log(`[POLL] Complete, stopping`);
          setIsGenerating(false);
          return;
        }

        if (status.status === 'failed' || status.status === 'cancelled') {
          console.log(`[POLL] Failed/Cancelled, stopping`);
          if (status.status === 'failed') {
            setError(status.errorMessage || 'Mockup generation failed');
          }
          setIsGenerating(false);
          return;
        }

        if (runId !== pollRunIdRef.current) {
          console.log(`[POLL] STALE before scheduling next, bailing out`);
          return;
        }
        
        console.log(`[POLL] Scheduling next poll in ${POLL_INTERVAL}ms`);
        pollTimeoutRef.current = setTimeout(() => pollJobStatus(jobId, runId), POLL_INTERVAL);
      }
    } catch (err) {
      console.error('[POLL] Error:', err);
      if (isMountedRef.current && runId === pollRunIdRef.current) {
        pollTimeoutRef.current = setTimeout(() => pollJobStatus(jobId, runId), POLL_INTERVAL);
      }
    }
  }, [onMockupsUpdated]);

  useEffect(() => {
    async function fetchCollections() {
      try {
        const response = await apiClient.get('/proxy/mockup-collections');
        const collections = response.data?.data?.collections;
        if (collections) {
          setCollections(collections);
          if (collections.length > 0) {
            setSelectedCollection(collections[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch collections:', err);
      } finally {
        setLoadingCollections(false);
      }
    }
    fetchCollections();
  }, []);

  useEffect(() => {
    let cancelled = false;
    console.log(`[EFFECT checkActiveJob] Running - productId:${productId}`);
    
    async function checkActiveJob() {
      try {
        const response = await apiClient.get(`/mockups/active?productId=${productId}`);
        if (cancelled) return;
        
        if (response.data.success && response.data.data) {
          const activeJob = response.data.data as JobStatus;
          console.log(`[EFFECT checkActiveJob] Found active job:${activeJob.jobId}`);
          setJobStatus(activeJob);
          setIsGenerating(true);
          
          pollRunIdRef.current += 1;
          console.log(`[EFFECT checkActiveJob] Starting poll with runId:${pollRunIdRef.current}`);
          pollJobStatus(activeJob.jobId, pollRunIdRef.current);
        } else {
          console.log(`[EFFECT checkActiveJob] No active job found`);
        }
      } catch (err) {
        console.error('Failed to check active job:', err);
      }
    }
    checkActiveJob();
    
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const startGeneration = async () => {
    if (!selectedCollection || !selectedRatio) return;

    if (!printFileUrls?.[selectedRatio]) {
      setError(`No print file available for ${selectedRatio} ratio`);
      return;
    }

    setError(null);
    setIsGenerating(true);
    setJobStatus(null);
    
    pollRunIdRef.current += 1;
    const runId = pollRunIdRef.current;
    console.log(`[startGeneration] Starting with runId:${runId}`);

    try {
      const response = await apiClient.post('/mockups/start', {
        productId,
        collection: selectedCollection,
        aspectRatio: selectedRatio,
      });

      if (response.data.success) {
        const { jobId, expectedCount } = response.data.data;
        console.log(`[startGeneration] Job created:${jobId}, starting poll with runId:${runId}`);
        setJobStatus({
          jobId,
          status: 'processing',
          expectedCount,
          completedCount: 0,
          mockupUrls: [],
        });
        pollJobStatus(jobId, runId);
      } else {
        setError(response.data.error || 'Failed to start generation');
        setIsGenerating(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start generation');
      setIsGenerating(false);
    }
  };

  const handleGenerateClick = () => {
    if (existingMockups.length > 0) {
      setShowWarning(true);
    } else {
      startGeneration();
    }
  };

  const cancelGeneration = async () => {
    if (!jobStatus?.jobId) return;

    const jobId = jobStatus.jobId;
    const mockupsBeforeCancel = jobStatus.mockupUrls || [];
    
    pollRunIdRef.current += 1;
    console.log(`[CANCEL] Cancelling - new runId:${pollRunIdRef.current} mockupsToKeep:${mockupsBeforeCancel.length}`);
    
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    
    setIsGenerating(false);
    setJobStatus(null);
    onMockupsUpdated(mockupsBeforeCancel);

    try {
      await apiClient.post('/mockups/cancel', { jobId });
    } catch (err) {
      console.error('Failed to cancel job:', err);
    }
  };

  const progressPercent = jobStatus
    ? (jobStatus.completedCount / jobStatus.expectedCount) * 100
    : 0;

  const availableRatios = PRINT_RATIOS.filter(
    (ratio) => printFileUrls?.[ratio]
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          Mockup Generator
        </CardTitle>
        <CardDescription>
          Generate marketing mockups for your product
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 p-3 rounded-md">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {!isGenerating && !jobStatus?.status?.includes('complete') && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Collection</label>
                <Select
                  value={selectedCollection}
                  onValueChange={setSelectedCollection}
                  disabled={loadingCollections}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select collection" />
                  </SelectTrigger>
                  <SelectContent>
                    {collections.map((collection) => (
                      <SelectItem key={collection} value={collection}>
                        {toCapitalCase(collection)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Aspect Ratio</label>
                <Select
                  value={selectedRatio}
                  onValueChange={(v) => setSelectedRatio(v as PrintRatio)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRatios.map((ratio) => (
                      <SelectItem key={ratio} value={ratio}>
                        {ratio}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={handleGenerateClick}
              disabled={!selectedCollection || !selectedRatio || loadingCollections}
              className="w-full"
            >
              Generate Mockups
            </Button>
          </div>
        )}

        {isGenerating && jobStatus && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">
                  Generating mockups... {jobStatus.completedCount} of {jobStatus.expectedCount}
                </span>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={cancelGeneration}>
                Cancel
              </Button>
            </div>
            <Progress value={progressPercent} className="h-2" />
            <p className="text-xs text-muted-foreground">
              This may take several minutes. You can leave this page - mockups will continue generating.
            </p>
          </div>
        )}

        {jobStatus?.status === 'complete' && (
          <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-md">
            <CheckCircle2 className="h-4 w-4" />
            <span className="text-sm">
              Successfully generated {jobStatus.completedCount} mockups!
            </span>
          </div>
        )}

        {(() => {
          const mockupsToShow = isGenerating 
            ? (jobStatus?.mockupUrls || [])
            : (jobStatus?.mockupUrls?.length ? jobStatus.mockupUrls : existingMockups);
          
          if (!mockupsToShow || mockupsToShow.length === 0) return null;
          
          return (
            <>
              <div className="grid grid-cols-3 gap-2 mt-4">
                {mockupsToShow.map((url, index) => (
                  <div
                    key={index}
                    className="aspect-square bg-muted rounded-md overflow-hidden cursor-pointer group relative"
                    onClick={() => {
                      setLightboxIndex(index);
                      setLightboxOpen(true);
                    }}
                  >
                    <img
                      src={url}
                      alt={`Mockup ${index + 1}`}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Maximize2 className="h-6 w-6 text-white" />
                    </div>
                  </div>
                ))}
              </div>
              <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={mockupsToShow.map((url) => ({ src: url }))}
                index={lightboxIndex}
                styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.9)' } }}
                controller={{ closeOnBackdropClick: true }}
              />
            </>
          );
        })()}

        <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Replace existing mockups?</AlertDialogTitle>
              <AlertDialogDescription>
                This product already has {existingMockups.length} mockup(s). 
                Generating new mockups will delete all existing ones. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => {
                setShowWarning(false);
                startGeneration();
              }}>
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
